import { useEffect, useState } from "react";
import { Modal, Spinner } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { timeAgo, toast } from "../lib/app";
import "./connect.css";

interface Connection {
  id: string;
  label: string;
  createdAt: number;
  lastUsedAt: number | null;
}

/** A shell command with a Copy button. */
function Command({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="connect-cmd">
      <pre>{text}</pre>
      <button
        className="btn btn-sm"
        onClick={async () => {
          await navigator.clipboard.writeText(text).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/**
 * Keys that let Claude Code on someone's computer put changes from their local
 * copy straight into this project (see server/src/live.ts). Each key is shown
 * once, inside the commands that set Claude Code up.
 */
export function ConnectDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [base, setBase] = useState(location.origin);
  const [label, setLabel] = useState("");
  const [made, setMade] = useState<{ key: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ connections: Connection[]; base: string }>(`/api/projects/${projectId}/connections`)
      .then((r) => {
        setConnections(r.connections);
        setBase(r.base);
      })
      .catch((e) => {
        toast(errorMessage(e), "error");
        setConnections([]);
      });
  }, [projectId]);

  async function make() {
    setBusy(true);
    try {
      const name = label.trim() || "My computer";
      const r = await api.post<{ key: string; connections: Connection[]; base: string }>(`/api/projects/${projectId}/connections`, { label: name });
      setConnections(r.connections);
      setBase(r.base);
      setMade({ key: r.key, label: name });
      setLabel("");
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Connection) {
    try {
      const r = await api.del<{ connections: Connection[] }>(`/api/projects/${projectId}/connections/${c.id}`);
      setConnections(r.connections);
      toast(`Removed the key for ${c.label || "that computer"}`);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  const download = made && `mkdir -p ~/.benleaf && curl -fsS -H "Authorization: Bearer ${made.key}" -o ~/.benleaf/benleaf-mcp.mjs ${base}/api/live/connector.mjs`;
  const add = made && `claude mcp add benleaf -e BENLEAF_URL=${base} -e BENLEAF_KEY=${made.key} -e BENLEAF_DIR="$PWD" -- node ~/.benleaf/benleaf-mcp.mjs`;

  return (
    <Modal title="Connect Claude Code" onClose={onClose} width={600}>
      <div className="stack connect">
        <p style={{ margin: 0 }}>
          Let Claude Code on your computer put its changes straight into this project. They go in as your edits, just as if you'd typed them, and everyone
          here sees Claude's cursor while it writes. A version is saved once things go quiet, like any other edit.
        </p>

        {made ? (
          <div className="sync-status">
            <div className="grow stack" style={{ gap: 10 }}>
              <div>
                In a terminal, go to your copy of this project (a <b>Download → Source</b> zip, or an empty folder) and run these two commands. The key is
                in them and won't be shown again.
              </div>
              <Command text={download!} />
              <Command text={add!} />
              <div className="muted small">
                Then start Claude Code there and ask it to <i>pull from benleaf</i>, make its changes, and <i>push to benleaf</i>. It needs Node 18 or newer.
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <input
              placeholder="Which computer is this for? (e.g. MacBook)"
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && make()}
            />
            <button className="btn btn-primary" disabled={busy} onClick={make}>
              Make a key
            </button>
          </div>
        )}

        <div>
          <div className="field-label">Your keys for this project</div>
          {connections === null ? (
            <Spinner />
          ) : connections.length === 0 ? (
            <div className="muted small">None yet.</div>
          ) : (
            connections.map((c) => (
              <div key={c.id} className="row connect-key">
                <div className="grow">
                  <div>{c.label || "Unnamed"}</div>
                  <div className="muted small">
                    Made {timeAgo(c.createdAt)} · {c.lastUsedAt ? `last used ${timeAgo(c.lastUsedAt)}` : "not used yet"}
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => remove(c)}>
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          A key only works for this project, and only while you can edit it. Changing your password removes all your keys.
        </p>
      </div>
    </Modal>
  );
}
